import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Typography, Card, CardContent, Divider } from '@mui/material';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { getQuestionnaireAnswerByUUID } from '../../../services/questionnaire';
import QuestionnaireViewer from '../QuestionnaireViewer/QuestionnaireViewer';
import PatientSectionHeading from '../SeeUser/components/PatientSectionHeading';

import ebrc from './templates/ebrc.json';
import ed5q from './templates/ed5q.json';
import eus from './templates/eus.json';
import ipaq from './templates/ipaq.json';
import qald from './templates/qald.json';
import iife5 from './templates/iife5.json';
import sde from './templates/sde.json';

const questionnairesMap = {
    [ebrc.id]: ebrc,
    [ed5q.id]: ed5q,
    [eus.id]: eus,
    [ipaq.id]: ipaq,
    [qald.id]: qald,
    [iife5.id]: iife5,
    [sde.id]: sde
};

const QuestionnaireAnswerDetails = () => {
    const { uuid } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { from } = location.state || {};
    const [answerData, setAnswerData] = useState(null);
    const [questionnaireData, setQuestionnaireData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchAnswer = async () => {
            try {
                const response = await getQuestionnaireAnswerByUUID(uuid);
                setAnswerData(response);

                const qId = response.questionnaire?.uuid;
                const q = questionnairesMap[qId];

                if (!q) {
                    console.warn('Questionário não encontrado para uuid:', qId);
                }

                setQuestionnaireData(q);
            } catch (error) {
                console.error('Erro ao buscar resposta do questionário:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchAnswer();
    }, [uuid]);

    if (loading) return <Typography>{t('questionnaires.loading_answers')}</Typography>;

    if (!answerData || !questionnaireData)
        return <Typography>{t('questionnaires.data_not_found')}</Typography>;

    const answersMap = {};
    answerData.answers.values.forEach((item) => {
        answersMap[item.id] = item.value;
    });

    const formWithAnswers = questionnaireData.steps
        .filter((step) => step.answerFormat)
        .map((step) => ({
            ...step,
            answer: answersMap[step.id]
        }));

    const patientUuid = answerData.patient?.role_uuid;
    const handleBack = () => {
        if (from) {
            navigate(from.pathname, { state: from.state });
        } else if (patientUuid) {
            navigate(`/users/user/${patientUuid}/questionarios`);
        } else {
            navigate(-1);
        }
    };

    return (
        <Card sx={{ m: 2 }}>
            <CardContent>
                <PatientSectionHeading
                    sectionLabel={t('userPage.sections.questionnaires')}
                    detailLabel={t('userPage.sections.questionnaireAnswer')}
                    onBack={() => navigate(`/users/user/${patientUuid}`)}
                    onSectionBack={handleBack}
                />
                <Typography variant="h4" gutterBottom>
                    {answerData.questionnaire.name}
                </Typography>
                <Typography variant="body1" gutterBottom>
                    {t('questionnaires.filled_by', {
                        name: answerData.filled_by.name,
                        role: answerData.filled_by.role
                    })}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('questionnaires.date', {
                        date: dayjs(answerData.ts).format('DD/MM/YYYY HH:mm')
                    })}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <QuestionnaireViewer
                    title={answerData.questionnaire.name}
                    description={t('questionnaires.description_filled', {
                        name: answerData.filled_by.name,
                        date: dayjs(answerData.ts).format('DD/MM/YYYY')
                    })}
                    formData={formWithAnswers}
                    previewMode={true}
                />
            </CardContent>
        </Card>
    );
};

export default QuestionnaireAnswerDetails;
